import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import KaboodleToaster from "components/common/KaboodleToaster";
import Table from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { Select } from "components/inputs";
import type { OptionType } from "components/inputs/Select/Select";
import { omit } from "lodash";
import { useEffect, useMemo, useState } from "react";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import {
  useCreateRxPayHistoryMutation,
  useDeletePayHistoryDocumentMutation,
  useDeleteRxPayHistoryMutation,
  useGetRxPayHistoryQuery,
  useUpdateRxPayHistoryMutation,
} from "services/future-medical-api";

import { getServiceField } from "../../future-medical-utils";
import ExtractPayHistoryModal from "../pay-history/ExtractPayHistoryModal";
import type { MedicalHistoryTableProps } from "../medicalRecordUtils";
import {
  createStatusIndicatorCell,
  getErrorMessage,
  getStatusColor,
  statusColumnProps,
} from "../medicalRecordUtils";
import RxHistoryModal from "./RxHistoryModal";
import type { ApiError } from "types/ApiTypes";

import type { RxPayHistoryRow } from "types/DataTypes";
import type { PaginatedResponse } from "types/DataTypes";

const RxHistoryTable = ({ service }: MedicalHistoryTableProps) => {
  const [isRxHistoryModalOpen, setIsRxHistoryModalOpen] = useState(false);
  const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
  const [isDeleteDocModalOpen, setIsDeleteDocModalOpen] = useState(false);
  const [selectedRxHistory, setSelectedRxHistory] = useState<
    RxPayHistoryRow | undefined
  >();
  const [selectedDocumentFilter, setSelectedDocumentFilter] =
    useState<OptionType | null>(null);

  const serviceId = service.id.toString();
  const fileId = service.file?.id;

  const {
    data: rxPayHistoryData,
    isLoading,
    refetch,
  } = useGetRxPayHistoryQuery(
    {
      ...getServiceField(service, true),
    },
    {
      skip: !serviceId,
    },
  );

  const { data: bgJobsData } = useGetBackgroundJobsQuery(
    {
      filters: {
        file_id: fileId ? String(fileId) : "",
        limit: 10,
        offset: 0,
      },
    },
    {
      skip: !fileId,
      pollingInterval: 3000,
    },
  );

  const hasActiveJob = useMemo(() => {
    return Boolean(
      bgJobsData?.results?.some(
        (job) =>
          (job.job_type === "pay_history_extraction" ||
            job.job_type === "pay_history_analysis" ||
            job.job_type === "Pay History Extraction" ||
            job.job_type === "Pay History Analysis") &&
          (job.status === "pending" || job.status === "in_progress"),
      ),
    );
  }, [bgJobsData]);

  const [prevHasActiveJob, setPrevHasActiveJob] = useState(false);

  useEffect(() => {
    if (prevHasActiveJob && !hasActiveJob) {
      refetch();
    }
    setPrevHasActiveJob(hasActiveJob);
  }, [hasActiveJob, prevHasActiveJob, refetch]);

  const rawRows = useMemo(
    () =>
      (rxPayHistoryData as PaginatedResponse<RxPayHistoryRow>)?.results || [],
    [rxPayHistoryData],
  );

  const formatHeaderDate = (dateStr?: string | null) => {
    if (!dateStr) return "";
    const clean = dateStr.substring(0, 10);
    const parts = clean.split("-");
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return clean;
  };

  const documentMap = useMemo(() => {
    const map = new Map<
      number,
      { filename: string; doi?: string; claimNumber?: string }
    >();

    rawRows.forEach((row) => {
      const docId = row.source_document?.id ?? row.source_document_id;
      if (docId != null && !map.has(docId)) {
        const filename =
          row.source_document?.original_filename_as_uploaded ||
          row.source_document?.original_filename ||
          `Document #${docId}`;
        const doiDate = row.date_of_injury?.date
          ? formatHeaderDate(row.date_of_injury.date)
          : undefined;
        const claimNo = row.claim_number?.number || undefined;

        map.set(docId, {
          filename,
          doi: doiDate,
          claimNumber: claimNo,
        });
      }
    });

    return map;
  }, [rawRows]);

  const documentOptions = useMemo(() => {
    if (documentMap.size <= 1) {
      return [];
    }

    const options: OptionType[] = [{ label: "All Documents", value: "all" }];

    documentMap.forEach((info, id) => {
      const details: string[] = [];
      if (info.doi) details.push(`DOI: ${info.doi}`);
      if (info.claimNumber) details.push(`Claim #: ${info.claimNumber}`);
      const label =
        details.length > 0
          ? `${info.filename} (${details.join(" | ")})`
          : info.filename;
      options.push({ label, value: id.toString() });
    });

    return options;
  }, [documentMap]);

  const selectedDocumentOption = useMemo(() => {
    if (!documentOptions.length) return null;
    return (
      documentOptions.find(
        (opt) => opt.value === selectedDocumentFilter?.value,
      ) || documentOptions[0]
    );
  }, [documentOptions, selectedDocumentFilter]);

  const activeDocumentHeaderInfo = useMemo(() => {
    if (selectedDocumentOption && selectedDocumentOption.value !== "all") {
      return documentMap.get(Number(selectedDocumentOption.value)) ?? null;
    }
    if (documentMap.size === 1) {
      return Array.from(documentMap.values())[0] ?? null;
    }
    return null;
  }, [documentMap, selectedDocumentOption]);

  const filteredRows = useMemo(() => {
    if (!selectedDocumentOption || selectedDocumentOption.value === "all") {
      return rawRows;
    }
    const filterId = Number(selectedDocumentOption.value);
    return rawRows.filter((row) => {
      const docId = row.source_document?.id ?? row.source_document_id;
      return docId === filterId;
    });
  }, [rawRows, selectedDocumentOption]);

  const [createRxHistory] = useCreateRxPayHistoryMutation();
  const [updateRxHistory] = useUpdateRxPayHistoryMutation();
  const [deleteRxHistory] = useDeleteRxPayHistoryMutation();
  const [deleteDocument, { isLoading: isDeletingDoc }] =
    useDeletePayHistoryDocumentMutation();

  const handleDeleteDocument = async () => {
    if (!selectedDocumentOption || selectedDocumentOption.value === "all")
      return;
    try {
      await deleteDocument({
        document_id: Number(selectedDocumentOption.value),
      }).unwrap();
      KaboodleToaster.success("Document deleted successfully");
      setSelectedDocumentFilter(null);
      setIsDeleteDocModalOpen(false);
    } catch (error) {
      console.error("Delete Document Error:", error);
      KaboodleToaster.error(
        getErrorMessage(error as ApiError, "Failed to delete document"),
      );
    }
  };

  const handleCreateRxHistory = async (data: Partial<RxPayHistoryRow>) => {
    try {
      await createRxHistory({
        data: {
          ...data,
          ...getServiceField(service, false),
        },
      }).unwrap();

      setIsRxHistoryModalOpen(false);
      KaboodleToaster.success("RX history created successfully");
    } catch (error) {
      console.error("Failed to create RX history:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to create RX history",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleEditRxHistory = async (data: Partial<RxPayHistoryRow>) => {
    if (!selectedRxHistory) return;

    try {
      await updateRxHistory({
        id: selectedRxHistory.id.toString(),
        data: omit(data, ["msa", "cost_projection"]),
      }).unwrap();

      setIsRxHistoryModalOpen(false);
      setSelectedRxHistory(undefined);
      KaboodleToaster.success("RX history updated successfully");
    } catch (error) {
      console.error("Failed to update RX history:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to update RX history",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleDeleteRxHistory = async (id: string) => {
    try {
      await deleteRxHistory({
        id,
        ...getServiceField(service, true),
      }).unwrap();

      KaboodleToaster.success("RX history deleted successfully");
    } catch (error) {
      console.error("Delete Error:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to delete RX history",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleRxRowClick = (row: RxPayHistoryRow) => {
    setSelectedRxHistory(row);
    setIsRxHistoryModalOpen(true);
  };

  const handleAddButtonClick = () => {
    setSelectedRxHistory(undefined);
    setIsRxHistoryModalOpen(true);
  };

  const handleModalClose = () => {
    setIsRxHistoryModalOpen(false);
    setSelectedRxHistory(undefined);
  };

  const rxHistoryColumns = [
    {
      ...statusColumnProps,
      cellClass: (row: RxPayHistoryRow) => getStatusColor(row.status),
      renderCell: () => createStatusIndicatorCell(),
      ...commonColumnProps,
      width: "5%",
    },
    {
      key: "date_filled",
      name: "Fill Date",
      width: "12%",
      minWidth: 120,
    },
    {
      key: "date_paid",
      name: "Paid Date",
      width: "12%",
      minWidth: 120,
    },
    {
      key: "pharmacy_name",
      name: "Pharmacy",
      width: "20%",
      minWidth: 200,
    },
    {
      key: "drug_name",
      name: "Drug Name",
      width: "30%",
      minWidth: 275,
    },
    {
      key: "amount",
      name: "Amount",
      width: "10%",
      minWidth: 100,
    },
    {
      key: "rx_number",
      name: "RX Number",
      width: "15%",
      minWidth: 120,
    },
  ] as const;

  return (
    <>
      <div
        className="data-block-header flex ai-c jc-sb"
        style={{
          padding: "15px 12px 7px 12px",
          margin: 0,
          overflowX: "auto",
          maxWidth: "100%",
          whiteSpace: "nowrap",
          gap: "8px",
        }}
      >
        <div className="flex ai-c" style={{ gap: "12px", flexShrink: 0 }}>
          <div className="swim-headers" style={{ flexShrink: 0 }}>
            RX History
          </div>
          {activeDocumentHeaderInfo && (
            <div
              className="flex ai-c"
              style={{
                fontSize: "12px",
                color: "#333",
                gap: "10px",
                fontWeight: 500,
                backgroundColor: "#f1f3f5",
                padding: "3px 10px",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              <span>
                <strong>Document:</strong> {activeDocumentHeaderInfo.filename}
              </span>
              {activeDocumentHeaderInfo.doi && (
                <span>
                  <strong>DOI:</strong> {activeDocumentHeaderInfo.doi}
                </span>
              )}
              {activeDocumentHeaderInfo.claimNumber && (
                <span>
                  <strong>Claim #:</strong>{" "}
                  {activeDocumentHeaderInfo.claimNumber}
                </span>
              )}
            </div>
          )}
          {documentOptions.length > 0 && (
            <div className="flex ai-c" style={{ gap: "8px", flexShrink: 0 }}>
              <div
                style={{
                  width: 320,
                  fontSize: "12px",
                  textTransform: "none",
                  fontWeight: "normal",
                }}
              >
                <Select
                  name="documentFilter"
                  options={documentOptions}
                  value={selectedDocumentOption}
                  onChange={(opt) =>
                    setSelectedDocumentFilter(
                      (opt as OptionType | null) ?? null,
                    )
                  }
                  isClearable={false}
                  isSearchable={false}
                  placeholder="Select Document"
                />
              </div>
              {selectedDocumentOption &&
                selectedDocumentOption.value !== "all" && (
                  <ButtonIcon
                    kind="error"
                    size="small"
                    title="Delete selected document"
                    onClick={() => setIsDeleteDocModalOpen(true)}
                    style={{
                      color: "#d9534f",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                      fontSize: "20px",
                      alignSelf: "center",
                    }}
                  >
                    delete
                  </ButtonIcon>
                )}
            </div>
          )}
        </div>
        <div
          className="pay-history-header-actions flex ai-c"
          style={{
            flexShrink: 0,
            gap: "8px",
            marginLeft: "auto",
          }}
        >
          <Button
            className="extract-pay-history-button"
            onClick={() => setIsExtractModalOpen(true)}
          >
            Extract Pay History Document
          </Button>
          <ButtonIcon
            kind="neutral"
            size="small"
            title="Add RX history entry"
            onClick={handleAddButtonClick}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              fontSize: "20px",
              alignSelf: "center",
            }}
          >
            add_circle
          </ButtonIcon>
        </div>
      </div>

      <div className="table-container">
        <Table<RxPayHistoryRow>
          className="msa-rx-history"
          columns={rxHistoryColumns}
          rows={filteredRows}
          rowKeyGetter={(row) => row.id.toString()}
          isLoading={isLoading}
          onSelectedRowsChange={() => {}}
          onCellClick={({ row }) => handleRxRowClick(row)}
          isResizable
          isSortable
        />
      </div>

      <RxHistoryModal
        isOpen={isRxHistoryModalOpen}
        onClose={handleModalClose}
        onSubmit={
          selectedRxHistory ? handleEditRxHistory : handleCreateRxHistory
        }
        onDelete={handleDeleteRxHistory}
        initialData={selectedRxHistory}
        title={selectedRxHistory ? "Edit RX History" : "Add RX History"}
        service={service}
      />
      <ExtractPayHistoryModal
        isOpen={isExtractModalOpen}
        onClose={() => setIsExtractModalOpen(false)}
        service={service}
      />
      <KaboodleModal
        isOpen={isDeleteDocModalOpen}
        onClose={() => setIsDeleteDocModalOpen(false)}
        footer={
          <div className="flex jc-c" style={{ gap: "10px" }}>
            <Button
              onClick={handleDeleteDocument}
              disabled={isDeletingDoc}
              style={{
                backgroundColor: "#d9534f",
                color: "#fff",
                borderColor: "#d43f3a",
              }}
            >
              {isDeletingDoc ? "Deleting..." : "Delete Document"}
            </Button>
            <Button
              onClick={() => setIsDeleteDocModalOpen(false)}
              disabled={isDeletingDoc}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ padding: "16px" }}>
          <h3 style={{ marginTop: 0 }}>Delete Document</h3>
          <p>
            Are you sure you want to delete document{" "}
            <strong>{selectedDocumentOption?.label}</strong>?
          </p>
          <p style={{ color: "#666", fontSize: "13px" }}>
            This will permanently delete all medical and Rx pay history items
            extracted from it.
          </p>
        </div>
      </KaboodleModal>
    </>
  );
};

export default RxHistoryTable;
